import { useFormikContext } from 'formik'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  getBonusDepositAmount,
  getDepositDestinationId,
  getDestinationCurrencyId,
  shouldAttemptBonusCampaignCheck,
} from '@pages/Traders-Room/Deposit/Amount/helpers'
import { DepositFormValues } from '@pages/Traders-Room/Deposit/DepositFlowLayout/types'
import { DepositTarget } from '@pages/Traders-Room/Deposit/hooks/types'

import { IconButton } from '@ui/components/Button'
import { Tooltip } from '@ui/components/Tooltip'
import { InfoIcon } from '@ui/icons/Info.icon'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useDebounceEffect } from '@utils/debounce'

import type { WalletDepositBonusCampaignDto } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

export const useDepositBonusCampaigns = (params: { target: DepositTarget }) => {
  const { target } = params
  const apiClient = useApiClient(ClientApiClient)

  const [isBonusLoading, setIsBonusLoading] = useState(false)
  const [availableBonusCampaigns, setAvailableBonusCampaigns] = useState<
    WalletDepositBonusCampaignDto['availableBonusCampaigns'] | null
  >(null)

  const { formatMoney } = useFormatNumber()
  const { t } = useTranslation()
  const { values, setFieldValue } = useFormikContext<DepositFormValues>()

  const paymentProvider = values.paymentProvider
  const providerCurrencyId = paymentProvider?.currency?.id || ''

  const destinationCurrencyId = getDestinationCurrencyId({ target, values })

  const depositAmount = useMemo(
    () =>
      getBonusDepositAmount({
        amount: values.amount,
        providerCurrencyId,
        destinationCurrencyId,
        conversionRate: values.conversionRate,
      }),
    [destinationCurrencyId, providerCurrencyId, values.amount, values.conversionRate]
  )

  const enabled = useMemo(
    () => shouldAttemptBonusCampaignCheck({ target, values }),
    [target, values]
  )

  const walletId = useMemo(() => getDepositDestinationId({ target, values }), [target, values])

  const selectedBonusCampaign = useMemo(() => {
    if (!values.bonusCampaignId) return availableBonusCampaigns?.[0]
    return availableBonusCampaigns?.find((c) => c.bonusCampaign.id === values.bonusCampaignId)
  }, [availableBonusCampaigns, values.bonusCampaignId])

  const bonusDepositModalBody = useMemo(() => {
    if (!selectedBonusCampaign) return undefined

    const { minDepositAmount, maxBonusAmount, depositLessThanMinDeposit, bonusExceedsMaxBonus } =
      selectedBonusCampaign

    if (depositLessThanMinDeposit) {
      return t('Wallet.Increase your amount to at least', {
        amount: formatMoney(minDepositAmount, destinationCurrencyId),
      })
    }

    if (bonusExceedsMaxBonus) {
      return t('Wallet.The maximum amount of bonus you can get according', {
        amount: formatMoney(maxBonusAmount, destinationCurrencyId),
      })
    }

    return undefined
  }, [destinationCurrencyId, formatMoney, selectedBonusCampaign, t])

  const BonusInfoIcon = () => {
    return (
      <Tooltip content={bonusDepositModalBody} direction='bottom'>
        <IconButton.Ghost
          size='md'
          icon={{
            icon: InfoIcon,
            color: selectedBonusCampaign?.depositLessThanMinDeposit
              ? 'text-danger-high-em'
              : 'text-warning-high-em',
          }}
        />
      </Tooltip>
    )
  }

  const isBonusAmountOutOfRange = useMemo((): boolean => {
    if (!selectedBonusCampaign) return false
    const { depositLessThanMinDeposit, bonusExceedsMaxBonus } = selectedBonusCampaign
    return depositLessThanMinDeposit || bonusExceedsMaxBonus
  }, [selectedBonusCampaign])

  const run = useCallback(async () => {
    if (!enabled) {
      setAvailableBonusCampaigns(null)
      setIsBonusLoading(false)
      return
    }

    if (!walletId || !paymentProvider?.id || depositAmount == null || depositAmount <= 0) {
      setAvailableBonusCampaigns(null)
      return
    }

    setIsBonusLoading(true)
    try {
      const res = await apiClient.getWalletDepositBonusCampaigns(
        walletId,
        paymentProvider.id,
        depositAmount
      )
      setAvailableBonusCampaigns(res.availableBonusCampaigns ?? null)
    } catch {
      setAvailableBonusCampaigns(null)
    } finally {
      setIsBonusLoading(false)
    }
  }, [apiClient, depositAmount, enabled, paymentProvider?.id, walletId])

  useDebounceEffect(() => void run(), [run], 500)

  const isBonusDepositSectionVisible =
    enabled && availableBonusCampaigns && availableBonusCampaigns.length > 0

  const isBonusCampaignValid = useMemo(() => {
    if (!values.isBonusSelected || !isBonusDepositSectionVisible) return true
    if (!values.isBonusRulesAccepted) return false
    if (!values.bonusCampaignId) return false

    return !selectedBonusCampaign?.depositLessThanMinDeposit
  }, [
    isBonusDepositSectionVisible,
    selectedBonusCampaign?.depositLessThanMinDeposit,
    values.bonusCampaignId,
    values.isBonusRulesAccepted,
    values.isBonusSelected,
  ])

  useEffect(() => {
    if (availableBonusCampaigns) {
      setFieldValue('availableBonusCampaigns', availableBonusCampaigns)
    }
  }, [availableBonusCampaigns, setFieldValue])

  useEffect(() => {
    if (isBonusDepositSectionVisible && !values.bonusCampaignId && availableBonusCampaigns) {
      setFieldValue('bonusCampaignId', availableBonusCampaigns[0].bonusCampaign.id)
    }
  }, [availableBonusCampaigns, isBonusDepositSectionVisible, setFieldValue, values.bonusCampaignId])

  return {
    availableBonusCampaigns,
    isBonusLoading,
    isBonusDepositSectionVisible,
    isBonusCampaignValid,
    selectedBonusCampaign,
    info: isBonusAmountOutOfRange ? <BonusInfoIcon /> : null,
  }
}
